import React, { useState, useEffect } from "react";
import { Drawer, Descriptions, Spin, Badge, Tag, Typography, Space, Divider, Button, Tooltip, message } from "antd";
import { MailOutlined, PaperClipOutlined, UserOutlined, ClockCircleOutlined, SendOutlined, ExclamationCircleOutlined, TeamOutlined, DownloadOutlined, RetweetOutlined, SyncOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../../../utils/dateUtils";
import { mailboxService } from "../../../../api/enhancedApi";
import BulkEmailRecipientsTable from "./BulkEmailRecipientsTable";
import { useAuth } from "../../../../hooks/useAuth";

const { Text, Paragraph, Title } = Typography;

const getUnfinishedRecipientCount = (email) => {
    if (!email?.is_bulk_email) return 0;
    const recipients = email.bulk_email_recipients || [];
    if (recipients.length > 0) {
        return recipients.filter((item) => item.status && item.status !== "SENT").length;
    }
    const summary = email.bulk_email_summary || {};
    return (summary.pending_count || 0) + (summary.failed_count || 0) + (summary.sending_count || 0) + (summary.skipped_count || 0);
};

const formatFileSize = (bytes) => {
    if (bytes === 0 || !bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * 邮件详情抽屉组件
 *
 * @param {boolean} visible - 是否可见
 * @param {function} onClose - 关闭回调
 * @param {Object} email - 来自 OutboxList 的邮件对象（来自 GET /v1/mail/outbox 列表项，已含 template_available / sender_user_id 等字段）
 * @param {function} [onResend] - "基于此邮件重发"按钮点击回调，签名 (email) => void。
 *   D3 / FR-015 代发确认（跨用户重发对话框）由父级 OutboxList（T15B）统一处理，本组件只回调通知。
 */
const EmailDetailDrawer = ({ visible, onClose, email, onResend = null, onResumeUnfinished = null }) => {
    const { hasPermission } = useAuth();
    const canSendEmail = hasPermission("mail:send");
    const [loading, setLoading] = useState(false);
    const [emailDetail, setEmailDetail] = useState(null);

    // 加载邮件详情
    useEffect(() => {
        if (visible && email?.id) {
            loadEmailDetail(email.id);
        }
    }, [visible, email?.id]);

    const loadEmailDetail = async (emailId) => {
        setLoading(true);
        try {
            const response = await mailboxService.getEmailDetail(emailId);
            if (response?.data) {
                setEmailDetail(response.data);
            } else {
                setEmailDetail(email); // 使用基础信息
            }
        } catch (error) {
            console.error("获取邮件详情失败:", error);
            message.error("获取邮件详情失败");
            setEmailDetail(email); // 使用基础信息
        } finally {
            setLoading(false);
        }
    };

    // 获取状态配置
    const getStatusConfig = (status) => {
        const statusMap = {
            PendingSend: { status: "processing", text: "待发送", color: "blue" },
            Sent: { status: "success", text: "已发送", color: "green" },
            Failed: { status: "error", text: "发送失败", color: "red" },
        };
        return statusMap[status] || { status: "default", text: status || "未知", color: "default" };
    };

    const displayEmail = emailDetail || email;
    const statusConfig = displayEmail ? getStatusConfig(displayEmail.status) : null;
    const recipientDetails = displayEmail?.bulk_email_recipients ?? email?.bulk_email_recipients ?? [];
    const hasRecipientDetails = recipientDetails.length > 0;

    // ====== 发件箱邮件重发：按钮显隐 / 置灰规则（T08） ======
    // 数据源：优先用列表行传入的 email 对象（含 template_available / template_code），不依赖 emailDetail 返回值——避免 mailboxService.getEmailDetail 没返回这些新字段时按钮永远显示不正确
    const sourceEmail = email || displayEmail || {};
    const isPendingSend = sourceEmail.status === "PendingSend";
    const templateAvailable = sourceEmail.template_available !== false; // undefined / true → 可用；false → 下线
    const templateCode = sourceEmail.template_code; // T04 已在列表项里返回
    const isTemplateOffline = templateCode && !templateAvailable; // FR-013 / B2 置灰条件
    const unfinishedCount = getUnfinishedRecipientCount(sourceEmail);

    // 综合按钮 disabled + tooltip：按 spec T08 优先级 (a) 权限 > (b) PendingSend > (c) 模板下线
    let resendDisabled = false;
    let resendTooltip = null;
    if (!canSendEmail) {
        resendDisabled = true;
        resendTooltip = "您没有发件权限，请联系管理员";
    } else if (isPendingSend) {
        resendDisabled = true;
        resendTooltip = "邮件正在发送中，无法重发";
    } else if (isTemplateOffline) {
        resendDisabled = true;
        resendTooltip = `原模板（${templateCode}）已下线，无法重发`;
    }

    const handleResendClick = () => {
        if (resendDisabled || !onResend) return;
        // 把当前 email 对象原样回传给父组件，由父组件做 D3 代发确认 + 打开重发抽屉（T15B）
        onResend(sourceEmail);
    };

    const resendButton = onResend ? (
        <Tooltip title={resendTooltip} placement="bottomRight">
            {/* Button 在 disabled 状态下不触发 onMouseEnter，Tooltip 默认不显示——外面套一个 span 解决（Antd 官方推荐做法） */}
            <span style={{ display: "inline-block", cursor: resendDisabled ? "not-allowed" : "pointer" }}>
                <Button
                    type="primary"
                    icon={<RetweetOutlined />}
                    onClick={handleResendClick}
                    disabled={resendDisabled}
                    style={resendDisabled ? { pointerEvents: "none" } : undefined}
                >
                    基于此邮件重发
                </Button>
            </span>
        </Tooltip>
    ) : null;

    const resumeButton = onResumeUnfinished && sourceEmail.is_bulk_email && unfinishedCount > 0 ? (
        <Button
            icon={<SyncOutlined />}
            onClick={() => onResumeUnfinished(sourceEmail)}
        >
            补发未完成({unfinishedCount})
        </Button>
    ) : null;

    return (
        <Drawer
            title={
                <Space>
                    <MailOutlined />
                    <span>邮件详情</span>
                </Space>
            }
            extra={<Space>{resumeButton}{resendButton}</Space>}
            placement="right"
            width={displayEmail?.is_bulk_email ? 900 : 700}
            onClose={onClose}
            open={visible}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16 }}>正在加载邮件详情...</p>
                </div>
            ) : displayEmail ? (
                <div>
                    {/* 邮件基本信息 */}
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="邮件主题">
                            <Text strong>{displayEmail.subject || "无主题"}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="发件人">
                            <Space>
                                <UserOutlined />
                                <Text>{displayEmail.from_address || "-"}</Text>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="收件人">
                            {hasRecipientDetails ? (
                                <Text type="secondary">
                                    <TeamOutlined style={{ marginRight: 4 }} />
                                    详见下方收件人详情 ({recipientDetails.length} 人)
                                </Text>
                            ) : (
                                <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 3, expandable: true }}>
                                    {displayEmail.to_address || "-"}
                                </Paragraph>
                            )}
                        </Descriptions.Item>
                        {displayEmail.cc_address && (
                            <Descriptions.Item label="抄送">
                                <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2, expandable: true }}>
                                    {displayEmail.cc_address}
                                </Paragraph>
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="状态">
                            <Badge status={statusConfig.status} text={statusConfig.text} />
                            {displayEmail.is_bulk_email && (
                                <Tag color="purple" style={{ marginLeft: 8 }}>
                                    群发邮件
                                </Tag>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="创建时间">
                            <Space>
                                <ClockCircleOutlined />
                                <Text>{displayEmail.created_at ? formatDateTime(displayEmail.created_at) : "-"}</Text>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="发送时间">
                            <Space>
                                <SendOutlined />
                                <Text>{displayEmail.sent_at ? formatDateTime(displayEmail.sent_at) : <Text type="secondary">未发送</Text>}</Text>
                            </Space>
                        </Descriptions.Item>
                        {displayEmail.has_attachments && (
                            <Descriptions.Item label="附件">
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <Space>
                                        <PaperClipOutlined />
                                        <Text>{displayEmail.attachment_count || displayEmail.attachments?.length || 0} 个附件</Text>
                                    </Space>
                                    {displayEmail.attachments && displayEmail.attachments.length > 0 && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
                                            {displayEmail.attachments.map((att) => (
                                                <div key={att.id} style={{ display: "flex", alignItems: "center" }}>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        icon={<DownloadOutlined />}
                                                        onClick={() => {
                                                            if (att.download_url) {
                                                                window.open(att.download_url, "_blank");
                                                            } else {
                                                                message.error("无法下载附件: 未获取到下载链接");
                                                            }
                                                        }}
                                                        style={{ padding: 0, height: "auto", lineHeight: "inherit", textAlign: "left" }}
                                                    >
                                                        {att.filename}
                                                    </Button>
                                                    <Text type="secondary" style={{ marginLeft: 8, fontSize: "12px" }}>
                                                        ({formatFileSize(att.file_size)})
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Space>
                            </Descriptions.Item>
                        )}
                        {displayEmail.error_message && (
                            <Descriptions.Item label="错误信息">
                                <Space>
                                    <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                                    <Text type="danger">{displayEmail.error_message}</Text>
                                </Space>
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    {/* 逐收件人详情 */}
                    {hasRecipientDetails && (
                        <>
                            <Divider orientation="left">
                                <Space>
                                    <TeamOutlined style={{ color: "#722ed1" }} />
                                    <span>收件人详情</span>
                                </Space>
                            </Divider>
                            <BulkEmailRecipientsTable
                                recipients={recipientDetails}
                                summary={displayEmail.bulk_email_summary || email?.bulk_email_summary || {}}
                                showSummary={true}
                                compact={false}
                            />
                        </>
                    )}

                    {/* 邮件正文 */}
                    <Divider orientation="left">邮件正文</Divider>
                    <div
                        style={{
                            border: "1px solid #f0f0f0",
                            borderRadius: 4,
                            padding: 16,
                            maxHeight: 400,
                            overflow: "auto",
                            backgroundColor: "#fafafa",
                        }}
                    >
                        {displayEmail.body_html ? <div dangerouslySetInnerHTML={{ __html: displayEmail.body_html }} /> : displayEmail.body_text ? <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{displayEmail.body_text}</pre> : <Text type="secondary">无邮件正文</Text>}
                    </div>
                </div>
            ) : (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                    <Text type="secondary">请选择一封邮件查看详情</Text>
                </div>
            )}
        </Drawer>
    );
};

export default EmailDetailDrawer;
